<template>
    <div
        class="layout"
        :class="{
            'layout--background': route.path === '/' && featureFlag('home-background-color'),
            'layout--craft-theme': featureFlag('craft-theme')
        }"
        :data-menu-open="menuIsOpen"
    >
        <ClientOnly>
            <NuxtLoadingIndicator :color="loadingColor" />
        </ClientOnly>

        <section class="layout__header">
            <TheNavigation
                :disable-login="disableLogin"
                @menu-toggled="onMenuToggle"
            />
        </section>

        <BaseNotice
            v-if="disableLogin && noticeETA"
            :message="
                `The login system is currently undergoing maintenance and is not available.${noticeETA ? ' It will be back online on ' + noticeETA + '.' : ''}`
            "
            :readmore-link="noticeLink"
        />

        <slot />

        <section class="layout__footer">
            <LazyTheFooter hydrate-on-visible />
        </section>
    </div>
</template>

<script setup>
import { setVariablesFromConfigObject } from '@digitalnatives/css-variables';
import { formats, formatSingleDate } from '~/utils/date';
import LoginQuery from '~/graphql/queries/global/Login.graphql';
import { useSiteDataStore } from '~/store/siteDataStore';

const { activeSite } = useMultisite();
const loadingColor = ref('#CCC');
const isLoading = ref(true);

const { featureFlag } = useMultisite();
const route = useRoute();

// Todo - move to the useSiteInjection within Pip.
useCookiePurger(1);

onBeforeMount(() => {
    isLoading.value = true;

    const group = activeSite.value?.group ?? activeSite.value?.handle;
    const rtl = activeSite.value?.rtl ?? false;

    import(`@/assets/theme/${group}/index.js`)
        .then(({default: theme}) => {
            loadingColor.value = theme.color.primary;
            if (rtl) {
                theme.direction = 'rtl';
            }
            setVariablesFromConfigObject(theme);
        })
        .catch(() => {
            loadingColor.value = '#FE6B20';
            console.warn(`Theme '${group}' not found, using default theme.`);
        });

    if (rtl) {
        document.documentElement.setAttribute('dir', 'rtl');
    }

    document.body.setAttribute('data-theme', group);

    isLoading.value = false;
});

const menuIsOpen = ref(null);

function onMenuToggle(state) {
    menuIsOpen.value = state;
}

onMounted(() => {
    if (!navigator.serviceWorker?.getRegistrations()) {
        return;
    }
    navigator.serviceWorker.getRegistrations().then(async(registrations) => { // fixme: temp fix unregister invalid service workers
        if (registrations.length) {
            for (const registration of registrations) {
                await registration.unregister();
            }
            window.location.reload();
        }
    });
});

const {variables} = useCraftVariables();
delete variables.uri;
const {data: loginData} = await useLazyAsyncQuery({
    query: LoginQuery,
    variables
});

const disableLogin = computed(() => {
    return toValue(loginData)?.login?.hideLogin;
});

const noticeETA = computed(() => {
    const endOfDowntimeValue = toValue(loginData)?.login?.noticeEndDateOfDowntime;
    return endOfDowntimeValue ? formatSingleDate(endOfDowntimeValue, formats.dayMonth) : null;
});

const noticeLink = computed(() => {
    return toValue(loginData)?.login?.noticeReadMoreLink?.[0]?.uri;
});

// Provide/inject pattern for pageType
// Injected in TheSearchFormEnhanced.vue
// and [...slug].vue <- condition, situation, treatment filled here
const pageType = ref(null);

const updatePageType = (type) => {
    pageType.value = type;
};

provide('pageType', { pageType, updatePageType });

const craftThemeStyles = computed(() => {
    const { theme } = useSiteDataStore();

    if (featureFlag('craft-theme') && theme) {
        const filledThemeItems = Object.values(theme).filter(item => item !== null && item !== undefined);
        if (filledThemeItems.length === 0) {
            return {};
        }

        return {
            primary: theme.primaryColor || '',
            primaryDark: theme.primaryColorDark || '',
            primaryLight: theme.primaryColorLight || '',
            primaryText: theme.primaryTextColor || '',

            secondary: theme.secondaryColor || '',
            secondaryDark: theme.secondaryColorDark || '',
            secondaryLight: theme.secondaryColorLight || '',
            secondaryText: theme.secondaryTextColor || ''
        };
    }
    return {};
});
</script>

<style lang="less">
.layout {
    display: grid;
    grid-template-areas: 'header' 'notice' 'main' 'footer';
    grid-template-rows: auto auto 1fr auto;
    min-height: 100vh;
    gap: 0;
    margin: 0 auto;
    background: var(--color-lightest);
}

.layout--craft-theme {
    --color-primary: v-bind('craftThemeStyles.primary');
    --color-primary-dark: v-bind('craftThemeStyles.primaryDark');
    --color-primary-light: v-bind('craftThemeStyles.primaryLight');
    --color-primary-text: v-bind('craftThemeStyles.primaryText');

    --color-secondary: v-bind('craftThemeStyles.secondary');
    --color-secondary-dark: v-bind('craftThemeStyles.secondaryDark');
    --color-secondary-light: v-bind('craftThemeStyles.secondaryLight');
    --color-secondary-text: v-bind('craftThemeStyles.secondaryText');
}

.layout[data-menu-open=true] {
    &:before {
        content: '';
        display: block;
        position: fixed;
        z-index: 10;
        background: var(--color-black-30);
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
    }

    @media @q-xl-min {
        overflow: auto;

        &:before {
            content: none;
            display: none;
        }
    }
}

.layout__header {
    grid-area: header;
    position: sticky;
    top: 0;
    z-index: 100;
}

.layout__main {
    .grid-container();
    .vertical-padding();

    grid-area: main;
    justify-self: center;
}

.layout--background {
    background: var(--home-bg-color);
}

.layout__main--narrow {
    .grid-container(true, var(--grid-maxWidth-content));
}

.layout__main--centered {
    align-content: center;
}

.layout__main--no-top-padding {
    padding-top: 0;

    .error:first-child {
        .vertical-padding();
    }
}

.layout__footer {
    grid-area: footer;
}
</style>
